import React, { useState, useEffect } from 'react';
import { submitFeedback } from '../api';

const FeedbackForm = ({ isOpen, onClose, onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    user_name: '',
    user_email: '',
    feedback_type: 'general_feedback',
    subject: '',
    description: '',
    priority: 'medium',
    category: 'other'
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setFormData({
        user_name: '',
        user_email: '',
        feedback_type: 'general_feedback',
        subject: '',
        description: '',
        priority: 'medium',
        category: 'other'
      });
      setMessage('');
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    
    if (formData.user_email && !isValidEmail(formData.user_email)) {
      newErrors.user_email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      // Add browser and system info
      const feedbackWithSystemInfo = {
        ...formData,
        browser_info: `${navigator.userAgent}`,
        system_info: `${navigator.platform}`
      };

      const result = await submitFeedback(feedbackWithSystemInfo);
      
      setMessage(result.message);
      
      // Reset form on success
      if (result.success) {
        setFormData({
          user_name: '',
          user_email: '',
          feedback_type: 'general_feedback',
          subject: '',
          description: '',
          priority: 'medium',
          category: 'other'
        });
        
        // Notify parent component
        if (onSubmitSuccess) {
          onSubmitSuccess(result);
        }
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      }
      
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-modal">
      <div className="feedback-modal__container">
        {/* Header */}
        <div className="feedback-modal__header">
          <h2 className="feedback-modal__title">Submit Feedback</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="feedback-form">
          {/* User Information Section */}
          <div className="feedback-section feedback-section--contact">
            <h3 className="feedback-section__title">Contact Information</h3>
            <div className="feedback-section__grid">
              <div className="feedback-field">
                <label className="feedback-field__label">
                  Name (Optional)
                </label>
                <input
                  type="text"
                  name="user_name"
                  value={formData.user_name}
                  onChange={handleInputChange}
                  className="feedback-field__input"
                  placeholder="Your name"
                />
              </div>
              
              <div className="feedback-field">
                <label className="feedback-field__label">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  name="user_email"
                  value={formData.user_email}
                  onChange={handleInputChange}
                  className={`feedback-field__input ${
                    errors.user_email ? 'feedback-field__input--error' : ''
                  }`}
                  placeholder="your.email@example.com"
                />
                {errors.user_email && (
                  <p className="feedback-field__error">{errors.user_email}</p>
                )}
              </div>
            </div>
          </div>

          {/* Feedback Details Section */}
          <div className="feedback-section feedback-section--details">
            <h3 className="feedback-section__title">Feedback Details</h3>
            <div className="feedback-section__grid feedback-section__grid--two-columns">
              <div className="feedback-field">
                <label className="feedback-field__label">
                  Feedback Type *
                </label>
                <select
                  name="feedback_type"
                  value={formData.feedback_type}
                  onChange={handleInputChange}
                  className="feedback-field__select"
                >
                  <option value="general_feedback">General Feedback</option>
                  <option value="bug_report">Bug Report</option>
                  <option value="feature_request">Feature Request</option>
                  <option value="model_improvement">Model Improvement</option>
                </select>
              </div>
              
              <div className="feedback-field">
                <label className="feedback-field__label">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="feedback-field__select"
                >
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🟠 High</option>
                  <option value="critical">🔴 Critical</option>
                </select>
              </div>
            </div>
            
            <div className="feedback-field">
              <label className="feedback-field__label">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="feedback-field__select"
              >
                <option value="ui">User Interface</option>
                <option value="backend">Backend</option>
                <option value="ml_model">ML Model</option>
                <option value="data">Data</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Subject Section */}
          <div className="feedback-section feedback-section--subject">
            <h3 className="feedback-section__title">Subject</h3>
            <div className="feedback-field">
              <label className="feedback-field__label">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                className={`feedback-field__input ${
                  errors.subject ? 'feedback-field__input--error' : ''
                }`}
                placeholder="Brief summary of your feedback"
              />
              {errors.subject && (
                <p className="feedback-field__error">{errors.subject}</p>
              )}
            </div>
          </div>

          {/* Description Section */}
          <div className="feedback-section feedback-section--description">
            <h3 className="feedback-section__title">Description</h3>
            <div className="feedback-field">
              <label className="feedback-field__label">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className={`feedback-field__textarea ${
                  errors.description ? 'feedback-field__textarea--error' : ''
                }`}
                placeholder="Please provide detailed information about your feedback..."
              />
              {errors.description && (
                <p className="feedback-field__error">{errors.description}</p>
              )}
              <p className="feedback-field__help">
                Minimum 10 characters. Current: {formData.description.length}
              </p>
            </div>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`feedback-message ${
              message.includes('Error') ? 'feedback-message--error' : 'feedback-message--success'
            }`}>
              {message}
            </div>
          )}

          {/* Action Buttons */}
          <div className="feedback-actions">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="btn btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn--primary"
            >
              {loading ? (
                <>
                  <svg className="btn__spinner" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;

